/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetInternalSessionIssuerRequest,
  GetInternalSessionIssuerSecurity,
} from "../models/operations/getinternalsessionissuer.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildInternalSessionIssuerQuery,
  InternalSessionIssuerQueryData,
  prefetchInternalSessionIssuer,
  queryKeyInternalSessionIssuer,
} from "./internalSessionIssuer.core.js";
export {
  buildInternalSessionIssuerQuery,
  type InternalSessionIssuerQueryData,
  prefetchInternalSessionIssuer,
  queryKeyInternalSessionIssuer,
};

export type InternalSessionIssuerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * get internalSessionIssuers
 *
 * @remarks
 * Read the organization's internal session issuer: its state, the issuer URI to register with supported services and its published keys. Requires org:read.
 */
export function useInternalSessionIssuer(
  request?: GetInternalSessionIssuerRequest | undefined,
  security?: GetInternalSessionIssuerSecurity | undefined,
  options?: QueryHookOptions<
    InternalSessionIssuerQueryData,
    InternalSessionIssuerQueryError
  >,
): UseQueryResult<
  InternalSessionIssuerQueryData,
  InternalSessionIssuerQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildInternalSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * get internalSessionIssuers
 *
 * @remarks
 * Read the organization's internal session issuer: its state, the issuer URI to register with supported services and its published keys. Requires org:read.
 */
export function useInternalSessionIssuerSuspense(
  request?: GetInternalSessionIssuerRequest | undefined,
  security?: GetInternalSessionIssuerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    InternalSessionIssuerQueryData,
    InternalSessionIssuerQueryError
  >,
): UseSuspenseQueryResult<
  InternalSessionIssuerQueryData,
  InternalSessionIssuerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildInternalSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setInternalSessionIssuerData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: InternalSessionIssuerQueryData,
): InternalSessionIssuerQueryData | undefined {
  const key = queryKeyInternalSessionIssuer(...queryKeyBase);

  return client.setQueryData<InternalSessionIssuerQueryData>(key, data);
}

export function invalidateInternalSessionIssuer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "internalSessionIssuers",
      "get",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllInternalSessionIssuer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "internalSessionIssuers", "get"],
  });
}
